// admin/src/components/ProgramEditModal.jsx
import React, { useState, useEffect } from 'react';

const API_ROOT = process.env.REACT_APP_API_URL || 'http://localhost:5000';

// Bucket definitions matching your interest categories
const AREA_BUCKETS = {
  build_make: {
    label: '🔨 Build & Make',
    description: 'Create with tech, design, tinker, invent'
  },
  understand_discover: {
    label: '🔬 Understand & Discover',
    description: 'Analyze patterns, experiment, model, ask questions'
  },
  express_create: {
    label: '🎨 Express & Create',
    description: 'Tell stories, visualize, perform, inspire'
  },
  help_connect: {
    label: '❤️ Help & Connect',
    description: 'Care for people, guide, support, serve'
  },
  lead_organize: {
    label: '📊 Lead & Organize',
    description: 'Plan, manage, grow ideas, influence'
  },
  explore_experience: {
    label: '🌍 Explore & Experience',
    description: 'Work with people, travel, learn by doing'
  },
  protect_sustain: {
    label: '🛡️ Protect & Sustain',
    description: 'Defend, care for earth, design solutions'
  },
  think_compute: {
    label: '💻 Think & Compute',
    description: 'Solve problems, model systems, optimize'
  }
};

// Map interest tags to buckets (from your interestToBucket_v2.js)
const INTEREST_TO_BUCKET = {
  create_with_tech: 'build_make',
  design_things: 'build_make',
  tinker_systems: 'build_make',
  invent_new: 'build_make',
  
  analyze_patterns: 'understand_discover',
  experiment_test: 'understand_discover',
  model_the_world: 'understand_discover',
  ask_big_questions: 'understand_discover',
  
  tell_stories: 'express_create',
  visualize_ideas: 'express_create',
  perform_share: 'express_create',
  inspire_audience: 'express_create',
  
  care_for_people: 'help_connect',
  guide_and_teach: 'help_connect',
  listen_and_support: 'help_connect',
  serve_community: 'help_connect',
  
  plan_and_manage: 'lead_organize',
  grow_ideas: 'lead_organize',
  influence_others: 'lead_organize',
  analyze_strategies: 'lead_organize',
  
  work_with_people: 'explore_experience',
  travel_and_discover: 'explore_experience',
  learn_by_doing: 'explore_experience',
  connect_globally: 'explore_experience',
  
  defend_and_secure: 'protect_sustain',
  care_for_earth: 'protect_sustain',
  design_solutions: 'protect_sustain',
  analyze_risks: 'protect_sustain',
  
  solve_problems: 'think_compute',
  model_systems: 'think_compute',
  quantify_truth: 'think_compute',
  optimize_processes: 'think_compute'
};

/**
 * ProgramEditModal - Full-featured modal for creating/editing programs
 * Now includes degree level tagging section
 * 
 * Props:
 * - program: existing program object (or null for new)
 * - isOpen: boolean
 * - onClose: function
 * - onSave: async function(programData) => saved program
 * - tenantId: string (from context)
 * - adminSecret: string (from context)
 */
export default function ProgramEditModal({ program, isOpen, onClose, onSave, tenantId, adminSecret }) {
  const isNew = !program?._id;
  
  const [formData, setFormData] = useState({
    title: '',
    permalink: '',
    image: '',
    areasOfStudy: [],
    degreeLevelTags: [], // NEW: Degree level tags
    enabled: true
  });
  
  const [availableAreaTags, setAvailableAreaTags] = useState([]);
  const [groupedAreaTags, setGroupedAreaTags] = useState({});
  const [loadingAreaTags, setLoadingAreaTags] = useState(false);
  
  // NEW: Degree level tags state
  const [availableDegreeTags, setAvailableDegreeTags] = useState([]);
  const [loadingDegreeTags, setLoadingDegreeTags] = useState(false);
  
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  // Fetch area tags when modal opens and group them by bucket
  useEffect(() => {
    if (!isOpen) return;
    
    const fetchAreaTags = async () => {
      setLoadingAreaTags(true);
      try {
        const res = await fetch(
          `${API_ROOT}/api/tags?tenantId=${encodeURIComponent(tenantId)}&category=area`,
          {
            headers: {
              'x-admin-secret': adminSecret || ''
            }
          }
        );
        if (res.ok) {
          const tags = await res.json();
          setAvailableAreaTags(tags);
          
          // Group tags by bucket
          const grouped = {};
          tags.forEach(tag => {
            const bucket = INTEREST_TO_BUCKET[tag.slug] || 'other';
            if (!grouped[bucket]) grouped[bucket] = [];
            grouped[bucket].push(tag);
          });
          
          // Sort each bucket's tags alphabetically by name
          Object.keys(grouped).forEach(bucket => {
            grouped[bucket].sort((a, b) => (a.name || '').localeCompare(b.name || ''));
          });
          
          setGroupedAreaTags(grouped);
        } else {
          console.warn('Failed to load area tags');
          setAvailableAreaTags([]);
          setGroupedAreaTags({});
        }
      } catch (err) {
        console.error('Error loading area tags:', err);
        setAvailableAreaTags([]);
        setGroupedAreaTags({});
      } finally {
        setLoadingAreaTags(false);
      }
    };

    fetchAreaTags();
  }, [isOpen, tenantId, adminSecret]);

  // NEW: Fetch degree level tags when modal opens
  useEffect(() => {
    if (!isOpen) return;
    
    const fetchDegreeTags = async () => {
      setLoadingDegreeTags(true);
      try {
        const res = await fetch(
          `${API_ROOT}/api/tags/degree-levels?tenantId=${encodeURIComponent(tenantId)}`,
          {
            headers: {
              'x-admin-secret': adminSecret || ''
            }
          }
        );
        if (res.ok) {
          const data = await res.json();
          setAvailableDegreeTags(data.tags || []);
        } else {
          console.warn('Failed to load degree level tags');
          setAvailableDegreeTags([]);
        }
      } catch (err) {
        console.error('Error loading degree tags:', err);
        setAvailableDegreeTags([]);
      } finally {
        setLoadingDegreeTags(false);
      }
    };

    fetchDegreeTags();
  }, [isOpen, tenantId, adminSecret]);

  // Reset form when program changes or modal opens
  useEffect(() => {
    if (isOpen && program) {
      setFormData({
        title: program.title || '',
        permalink: program.permalink || '',
        image: program.image || '',
        areasOfStudy: Array.isArray(program.areasOfStudy) ? [...program.areasOfStudy] : [],
        degreeLevelTags: Array.isArray(program.degreeLevelTags) ? [...program.degreeLevelTags] : [], // NEW
        enabled: program.enabled !== false
      });
    } else if (isOpen && !program) {
      setFormData({
        title: '',
        permalink: '',
        image: '',
        areasOfStudy: [],
        degreeLevelTags: [], // NEW
        enabled: true
      });
    }
    setError('');
    setSaving(false);
  }, [isOpen, program]);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const toggleArea = (tagSlug) => {
    setFormData(prev => ({
      ...prev,
      areasOfStudy: prev.areasOfStudy.includes(tagSlug)
        ? prev.areasOfStudy.filter(a => a !== tagSlug)
        : [...prev.areasOfStudy, tagSlug]
    }));
  };

  // NEW: Toggle degree level tag
  const toggleDegreeLevel = (tagSlug) => {
    setFormData(prev => ({
      ...prev,
      degreeLevelTags: prev.degreeLevelTags.includes(tagSlug)
        ? prev.degreeLevelTags.filter(d => d !== tagSlug)
        : [...prev.degreeLevelTags, tagSlug]
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    
    if (!formData.title.trim()) {
      setError('Title is required');
      return;
    }

    setSaving(true);
    try {
      const payload = {
        ...formData,
        title: formData.title.trim(),
        permalink: formData.permalink.trim(),
        image: formData.image.trim()
      };
      
      if (program?._id) {
        payload._id = program._id;
      }

      await onSave(payload);
      onClose();
    } catch (err) {
      setError(err.message || 'Failed to save program');
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  // Get bucket keys in desired order
  const bucketOrder = [
    'build_make',
    'understand_discover',
    'express_create',
    'help_connect',
    'lead_organize',
    'explore_experience',
    'protect_sustain',
    'think_compute'
  ];

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 700 }}>
        <div className="modal-header">
          <h2>{isNew ? 'New Program' : 'Edit Program'}</h2>
          <button 
            className="modal-close" 
            onClick={onClose}
            aria-label="Close"
          >
            ×
          </button>
        </div>

        <div className="modal-body">
          {error && (
            <div className="error" role="alert" style={{ marginBottom: 16 }}>
              {error}
            </div>
          )}

          <div className="form-group">
            <label htmlFor="title">
              Title <span style={{ color: '#ef4444' }}>*</span>
            </label>
            <input
              id="title"
              type="text"
              className="input"
              value={formData.title}
              onChange={(e) => handleChange('title', e.target.value)}
              placeholder="e.g., Computer Science"
              required
              autoFocus
            />
          </div>

          <div className="form-group">
            <label htmlFor="permalink">Permalink (URL)</label>
            <input
              id="permalink"
              type="url"
              className="input"
              value={formData.permalink}
              onChange={(e) => handleChange('permalink', e.target.value)}
              placeholder="https://example.edu/programs/computer-science"
            />
          </div>

          <div className="form-group">
            <label htmlFor="image">Image URL</label>
            <input
              id="image"
              type="url"
              className="input"
              value={formData.image}
              onChange={(e) => handleChange('image', e.target.value)}
              placeholder="https://example.edu/images/cs-program.jpg"
            />
            {formData.image && (
              <div style={{ marginTop: 8 }}>
                <img 
                  src={formData.image} 
                  alt="Preview" 
                  style={{ 
                    maxWidth: '100%', 
                    maxHeight: 120, 
                    borderRadius: 8,
                    objectFit: 'cover' 
                  }}
                  onError={(e) => {
                    e.currentTarget.style.display = 'none';
                  }}
                />
              </div>
            )}
          </div>

          {/* NEW: Degree Level Section */}
          <div className="form-group">
            <label>
              Degree Levels
              {formData.degreeLevelTags.length > 0 && (
                <span style={{ 
                  marginLeft: 8,
                  fontSize: '0.875rem', 
                  color: '#0ea5e9', 
                  fontWeight: 600
                }}>
                  ({formData.degreeLevelTags.length} selected)
                </span>
              )}
            </label>
            <div style={{ fontSize: '0.8rem', color: '#6b7280', marginBottom: 8 }}>
              Select which degree levels this program offers
            </div>
            
            {loadingDegreeTags && (
              <div className="muted" style={{ fontSize: '0.875rem', marginTop: 8 }}>
                Loading degree level options...
              </div>
            )}

            {!loadingDegreeTags && availableDegreeTags.length === 0 && (
              <div className="adm-empty" style={{ fontSize: '0.875rem', marginTop: 8, color: '#ef4444' }}>
                ⚠️ No degree level tags found. Run the seed script to create them.
              </div>
            )}

            {!loadingDegreeTags && availableDegreeTags.length > 0 && (
              <div style={{ 
                display: 'flex',
                flexWrap: 'wrap',
                gap: 10,
                padding: 16,
                background: '#f9fafb',
                borderRadius: 8,
                border: '1px solid #e5e7eb'
              }}>
                {availableDegreeTags.map((tag) => {
                  const isSelected = formData.degreeLevelTags.includes(tag.slug);
                  
                  return (
                    <label 
                      key={tag.slug}
                      title={tag.description || tag.name}
                      style={{ 
                        display: 'flex', 
                        alignItems: 'center',
                        gap: 8,
                        cursor: 'pointer',
                        padding: '10px 16px',
                        borderRadius: '8px',
                        background: isSelected ? '#dbeafe' : 'white',
                        border: `2px solid ${isSelected ? '#3b82f6' : '#d1d5db'}`,
                        fontSize: '0.875rem',
                        fontWeight: isSelected ? 600 : 500,
                        color: isSelected ? '#1e40af' : '#374151',
                        transition: 'all 0.15s ease',
                        boxShadow: isSelected ? '0 1px 3px rgba(59, 130, 246, 0.2)' : 'none'
                      }}
                      onMouseEnter={(e) => {
                        if (!isSelected) {
                          e.currentTarget.style.borderColor = '#9ca3af';
                          e.currentTarget.style.background = '#f3f4f6';
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (!isSelected) {
                          e.currentTarget.style.borderColor = '#d1d5db';
                          e.currentTarget.style.background = 'white';
                        }
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => toggleDegreeLevel(tag.slug)}
                        style={{ 
                          cursor: 'pointer',
                          width: '18px',
                          height: '18px'
                        }}
                      />
                      <span>{tag.name}</span>
                      {tag.count > 0 && (
                        <span style={{ 
                          fontSize: '0.75rem',
                          color: isSelected ? '#60a5fa' : '#9ca3af',
                          marginLeft: 4
                        }}>
                          ({tag.count})
                        </span>
                      )}
                    </label>
                  );
                })}
              </div>
            )}

            {formData.degreeLevelTags.length === 0 && !loadingDegreeTags && availableDegreeTags.length > 0 && (
              <div style={{ 
                marginTop: 8,
                padding: '8px 12px',
                background: '#fef2f2',
                border: '1px solid #fecaca',
                borderRadius: 6,
                fontSize: '0.8rem',
                color: '#dc2626'
              }}>
                ⚠️ No degree levels selected - this program won't appear in degree-filtered searches
              </div>
            )}
          </div>

          {/* Areas of Study Section */}
          <div className="form-group">
            <label>
              Areas of Study 
              {formData.areasOfStudy.length > 0 && (
                <span style={{ 
                  marginLeft: 8,
                  fontSize: '0.875rem', 
                  color: '#0ea5e9', 
                  fontWeight: 600
                }}>
                  ({formData.areasOfStudy.length} selected)
                </span>
              )}
            </label>
            
            {loadingAreaTags && (
              <div className="muted" style={{ fontSize: '0.875rem', marginTop: 8 }}>
                Loading available tags...
              </div>
            )}

            {!loadingAreaTags && availableAreaTags.length === 0 && (
              <div className="adm-empty" style={{ fontSize: '0.875rem', marginTop: 8 }}>
                No area tags found. Create area tags first in the Tags manager.
              </div>
            )}

            {!loadingAreaTags && Object.keys(groupedAreaTags).length > 0 && (
              <div style={{ 
                marginTop: 12,
                maxHeight: '450px',
                overflowY: 'auto',
                border: '1px solid #e5e7eb',
                borderRadius: '10px',
                background: 'white'
              }}>
                {bucketOrder.map((bucketKey, idx) => {
                  const tags = groupedAreaTags[bucketKey];
                  if (!tags || tags.length === 0) return null;
                  
                  const bucketInfo = AREA_BUCKETS[bucketKey];
                  if (!bucketInfo) return null;

                  return (
                    <div 
                      key={bucketKey} 
                      style={{ 
                        padding: '16px',
                        borderBottom: idx < bucketOrder.length - 1 ? '1px solid #f3f4f6' : 'none',
                        background: idx % 2 === 0 ? 'white' : '#fafbfc'
                      }}
                    >
                      <div style={{ 
                        fontWeight: 700,
                        fontSize: '0.9rem',
                        marginBottom: 6,
                        color: '#111827',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 8
                      }}>
                        {bucketInfo.label}
                      </div>
                      <div style={{ 
                        fontSize: '0.75rem',
                        color: '#6b7280',
                        marginBottom: 12,
                        lineHeight: 1.4
                      }}>
                        {bucketInfo.description}
                      </div>
                      
                      <div style={{ 
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
                        gap: '8px'
                      }}>
                        {tags.map((tag) => {
                          const isSelected = formData.areasOfStudy.includes(tag.slug);
                          const hasDescription = tag.description && tag.description.trim();
                          
                          return (
                            <label 
                              key={tag._id || tag.slug}
                              title={hasDescription ? tag.description : undefined}
                              style={{ 
                                display: 'flex', 
                                alignItems: 'flex-start',
                                gap: 8,
                                cursor: 'pointer',
                                padding: '10px 12px',
                                borderRadius: '8px',
                                background: isSelected ? '#eff6ff' : 'white',
                                border: `1.5px solid ${isSelected ? '#3b82f6' : '#e5e7eb'}`,
                                transition: 'all 0.15s ease',
                                fontSize: '0.813rem',
                                minHeight: hasDescription ? '52px' : '38px',
                                boxShadow: isSelected ? '0 1px 3px rgba(59, 130, 246, 0.1)' : 'none'
                              }}
                              onMouseEnter={(e) => {
                                if (!isSelected) {
                                  e.currentTarget.style.borderColor = '#cbd5e1';
                                  e.currentTarget.style.background = '#f8fafc';
                                }
                              }}
                              onMouseLeave={(e) => {
                                if (!isSelected) {
                                  e.currentTarget.style.borderColor = '#e5e7eb';
                                  e.currentTarget.style.background = 'white';
                                }
                              }}
                            >
                              <input
                                type="checkbox"
                                checked={isSelected}
                                onChange={() => toggleArea(tag.slug)}
                                style={{ 
                                  margin: '2px 0 0 0', 
                                  cursor: 'pointer', 
                                  flexShrink: 0,
                                  width: '16px',
                                  height: '16px'
                                }}
                              />
                              <div style={{ flex: 1, minWidth: 0 }}>
                                <div style={{ 
                                  fontWeight: isSelected ? 600 : 500,
                                  color: isSelected ? '#1e40af' : '#374151',
                                  lineHeight: 1.3,
                                  wordBreak: 'break-word',
                                  marginBottom: hasDescription ? 4 : 0
                                }}>
                                  {tag.name}
                                </div>
                                {hasDescription && (
                                  <div style={{
                                    fontSize: '0.72rem',
                                    color: isSelected ? '#60a5fa' : '#6b7280',
                                    lineHeight: 1.3,
                                    wordBreak: 'break-word',
                                    fontStyle: 'italic'
                                  }}>
                                    {tag.description}
                                  </div>
                                )}
                              </div>
                            </label>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          <div className="form-group">
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={formData.enabled}
                onChange={(e) => handleChange('enabled', e.target.checked)}
              />
              <span>Enabled (visible to quiz users)</span>
            </label>
          </div>
        </div>

        <div className="modal-footer">
          <button
            type="button"
            className="btn"
            onClick={onClose}
            disabled={saving}
          >
            Cancel
          </button>
          <button
            type="button"
            className="btn btn--primary"
            onClick={handleSubmit}
            disabled={saving}
          >
            {saving ? 'Saving...' : isNew ? 'Create Program' : 'Save Changes'}
          </button>
        </div>
      </div>
    </div>
  );
}